import type { Dispatch, SetStateAction, ReactNode } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled/text';

import {
	useNav4CollapseReminderDismissal,
	nav4CollapseReminderI18n,
} from './nav4CollapseReminderHelpers.tsx';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

type Nav4CollapseReminderPopoverProps = {
	children: ReactNode;
	isVisible: boolean;
	setIsNav4CollapseReminderSpotlightActive: Dispatch<SetStateAction<boolean>>;
};

export const Nav4CollapseReminderPopover = ({
	children,
	isVisible,
	setIsNav4CollapseReminderSpotlightActive,
}: Nav4CollapseReminderPopoverProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { dismissSpotlight } = useNav4CollapseReminderDismissal(
		setIsNav4CollapseReminderSpotlightActive,
	);

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<PopoverContent isVisible={isVisible} placement="right-end" dismiss={dismissSpotlight}>
				<SpotlightCard testId="nav4-collapse-reminder-spotlight">
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(nav4CollapseReminderI18n.heading)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>
						<Text>{formatMessage(nav4CollapseReminderI18n.body)}</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={dismissSpotlight}>
								{formatMessage(nav4CollapseReminderI18n.dismissButton)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};
